import { CheckboxCustomEvent } from '@ionic/react';
import classNames from 'classnames';
import { FullPost, Poll, PollOption } from 'model';
import React, { ChangeEvent, useCallback } from 'react';
import { useUpdateDisplayCommentsMutation } from '../api/PostApi';
import { FormHookReturnType, FormValuesType } from '../hooks/useFormValues';
import { BigImageContainer } from './BigImageContainer';
import { Checkbox } from './Forms/Checkbox';
import { FormErrors } from './Forms/FormErrors';
import { FormTextarea } from './Forms/FormTextarea';
import { Input } from './Forms/Input';
import { TopicSearchBox } from './Forms/TopicSearchBox';
import { IconButton } from './IconButton';
import { IconAdd, IconClose } from './QuirkIcons';
import { UploadImageButton } from './UploadImageButton';
// Styles
import styles from './PollEditFields.module.css';
// eslint-disable-next-line css-modules/no-unused-class
import formStyles from './Forms/FormFields.module.css';
// eslint-disable-next-line css-modules/no-unused-class
// eslint-disable-next-line css-modules/no-unused-class
import postStyles from '../styles/posts.module.css';

export const PollEditFields: React.FC<{
	poll: FullPost;
	pollOptions: PollOption[];
	addOption: () => void;
	deleteOption: (i: string) => void;
	optionErrors: string[];
	setPollValues: (e: ChangeEvent<HTMLInputElement>) => void;
	setEvent: (value: FullPost | null | undefined) => void;
	setValues: FormHookReturnType<Poll>['setValues'];
	formValues: FormValuesType<Poll>;
}> = ({ setValues, formValues, poll, setEvent, pollOptions, optionErrors, setPollValues, addOption, deleteOption }) => {
	const [updateDisplayComments] = useUpdateDisplayCommentsMutation();
	const totalPollOptions = 6;
	const commentCheck = useCallback(
		async (e: CheckboxCustomEvent) => {
			setValues({ displayComments: e.target.checked });
			setEvent({ ...poll, displayComments: e.target.checked });
			await updateDisplayComments({ postid: poll._id, display: e.target.checked });
		},
		[poll, setEvent, setValues, updateDisplayComments]
	);

	return (
		<>
			<div className={formStyles.fieldsContainer}>
				{formValues && (
					<>
						<div className="relative">
							<FormTextarea
								id="contents"
								nogap
								setValues={setValues}
								field={formValues.contents}
								placeholder="Ask your poll question"
								label="Question"
								testId="editPostContentField"
							/>
						</div>
						<div className="space20" />
						{formValues.topic && (
							<TopicSearchBox
								label="Topic"
								topic={formValues.topic?.value}
								setValues={setValues}
								errors={formValues.topic?.errors}
								testId="editPostTopicField"
							/>
						)}
						<>
							<div className={styles.optionsHolder} style={{ height: pollOptions.length * 65 }}>
								{pollOptions.map((option: PollOption, i: number) => (
									<div
										key={option.key}
										className={classNames(
											i > 1 ? styles.numberedFieldDelete : styles.numberedField
										)}
									>
										<span className={styles.pollItemNumber}>{i + 1}</span>
										<Input
											type="text"
											onChange={setPollValues}
											name={option.key}
											value={option.value}
											placeholder={'Option ' + (i + 1)}
											testId={`PollOption-${i}`}
										/>
										{i >= 2 && (
											<div>
												<IconButton
													icon={<IconClose />}
													onClick={() => deleteOption(option.key)}
												/>
											</div>
										)}
									</div>
								))}
							</div>
							<FormErrors errors={optionErrors} />
						</>

						<>
							<div
								className={styles.addButton}
								style={{ paddingTop: optionErrors.length > 0 ? '20px' : 0 }}
							>
								{pollOptions.length < totalPollOptions ? (
									<IconButton
										label="Add Option"
										icon={
											<span className="iconCircle">
												<IconAdd />
											</span>
										}
										onClick={addOption}
									/>
								) : (
									<div></div>
								)}
								{poll.images.length < 6 && <UploadImageButton post={poll} />}
							</div>
						</>

						{poll.images.length > 0 && (
							<div className={postStyles.postImageContainer}>
								<BigImageContainer post={poll} edit={true} images={poll.images} />
							</div>
						)}
					</>
				)}
				<div className={styles.postFooter}>
					<Checkbox onClick={commentCheck} value={poll.displayComments} label="Show Comments" />
				</div>
			</div>
		</>
	);
};
